import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { adminLoginWithGithub } from '#bv/lib/adminApi'
import { getSupabaseClient } from '#bv/lib/supabaseClient'

export const Route = createFileRoute('/admin/login')({
  component: AdminLoginPage,
})

function AdminLoginPage() {
  const navigate = useNavigate()
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const [forceReconnect, setForceReconnect] = useState(false)

  useEffect(() => {
    const run = async () => {
      const existingToken = sessionStorage.getItem('bv_admin_token')
      if (existingToken) {
        await navigate({ to: '/admin/dashboard' })
        return
      }

      const supabase = getSupabaseClient()
      let finished = false

      const exchangeAndGo = async () => {
        if (finished) return
        let { data: sessionWrap } = await supabase.auth.getSession()
        let session = sessionWrap.session
        const accessToken = session?.access_token
        if (!accessToken) return false

        const provider = session.user?.app_metadata?.provider
        if (provider && provider !== 'github') return false

        // Restored Supabase sessions often omit provider_token; refresh sometimes repopulates it for org checks.
        if (!session.provider_token) {
          const { data: refreshed } = await supabase.auth.refreshSession()
          session = refreshed.session ?? session
        }
        const githubProviderToken = session?.provider_token ?? undefined
        try {
          setErr('')
          const { token } = await adminLoginWithGithub({ data: { accessToken, githubProviderToken } })
          sessionStorage.setItem('bv_admin_token', token)
          try {
            sessionStorage.removeItem('bv_admin_oauth_pending')
          } catch {
            // ignore
          }
          finished = true
          await navigate({ to: '/admin/dashboard' })
          return true
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : String(e)
          if (msg.toLowerCase().includes('provider token missing')) {
            setForceReconnect(true)
            setErr(
              'GitHub did not send an organization token for this saved session (Supabase often drops it after refresh). Click below to sign in again. Your host can also set ADMIN_GITHUB_ORG_SERVICE_TOKEN so admin works without reconnecting.',
            )
            setLoading(false)
            return false
          }
          throw e
        }
      }

      try {
        const hadOAuthParams =
          window.location.search.includes('code=') ||
          window.location.search.includes('access_token=') ||
          window.location.hash.includes('access_token=')

        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href)
          const code = url.searchParams.get('code')
          if (code) {
            await supabase.auth.exchangeCodeForSession(code)
            url.searchParams.delete('code')
            window.history.replaceState(null, '', url.toString())
          }
        }

        const { data: sub } = supabase.auth.onAuthStateChange(() => {
          // OAuth callback can complete after initial render.
          void exchangeAndGo()
        })

        if (await exchangeAndGo()) return

        if (!hadOAuthParams) {
          setLoading(false)
          sub.subscription.unsubscribe()
          return
        }

        // Give Supabase a short window to finish token exchange.
        for (let i = 0; i < 12; i++) {
          await new Promise((r) => setTimeout(r, 250))
          if (await exchangeAndGo()) {
            sub.subscription.unsubscribe()
            return
          }
        }
        setLoading(false)
        sub.subscription.unsubscribe()
      } catch (e: unknown) {
        setErr(
          e instanceof Error
            ? e.message
            : 'Unable to initialize GitHub login. Check Supabase env variables and provider setup.'
        )
        setLoading(false)
      }
    }
    void run()
  }, [navigate])

  const signInWithGithub = async () => {
    setErr('')
    setLoading(true)
    try {
      const supabase = getSupabaseClient()
      // Mark that an admin OAuth flow was initiated, so the root token handler
      // can route "/" token hashes back to /admin/login reliably.
      try {
        sessionStorage.setItem('bv_admin_oauth_pending', '1')
      } catch {
        // ignore
      }
      // Clear stale provider sessions so GitHub re-consent can refresh provider_token.
      await supabase.auth.signOut()
      const redirectTo = `${window.location.origin}/admin/login`
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo,
          scopes: 'read:org',
          queryParams: {
            prompt: 'consent',
          },
        },
      })
      if (error) throw error
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Login failed')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#050508' }}>
      <div
        className="w-full max-w-md rounded-2xl border p-8 shadow-2xl"
        style={{
          borderColor: 'rgba(0,191,255,0.15)',
          background: 'linear-gradient(165deg, rgba(14,10,18,0.98), rgba(9,7,12,0.96))',
          boxShadow: '0 8px 36px rgba(0,0,0,0.45)',
        }}
      >
        <div className="mb-8 flex items-center gap-3">
          <img
            src="/pulsedigital-logo-mark.png"
            alt=""
            className="h-11 w-auto object-contain"
            style={{ filter: 'drop-shadow(0 0 8px rgba(0, 191, 255,0.35)) drop-shadow(0 0 12px rgba(123, 47, 190,0.25))' }}
          />
          <div>
            <h1 className="font-display text-2xl tracking-wide chrome-text">ADMIN</h1>
            <p className="text-xs" style={{ color: '#5a6d8a' }}>
              PulseDigital console
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: '#5a6d8a' }}>
            Sign in with your approved GitHub account (organization access required).
          </p>
          {err ? (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{err}</p>
          ) : null}
          <button
            type="button"
            onClick={() => void signInWithGithub()}
            disabled={loading}
            className="btn-primary w-full rounded-xl py-3 text-sm font-semibold disabled:opacity-50"
          >
            {loading ? 'Checking session…' : forceReconnect ? 'Reconnect GitHub' : 'Continue with GitHub'}
          </button>
        </div>

        <p className="mt-6 text-center text-xs" style={{ color: '#5a6d8a' }}>
          <Link to="/" className="text-[#8ecfff] hover:underline">
            ← Back to store
          </Link>
        </p>
      </div>
    </div>
  )
}
